/**
 * Perqen Black Friday gift: adds the free spray when the cart reaches the
 * threshold, removes it when the cart drops back below, and shows a progress
 * line in the cart drawer. Config comes from snippets/perqen-bf-gift.liquid,
 * which only renders this file inside the window, with the setting on, for
 * non-members.
 *
 * Rules (perqen-bf-offer-and-creative-20261030.md §4b):
 *  - the gift's own price never counts toward the threshold
 *  - never add if any line already holds the gift variant (bought or gifted)
 *  - lines we add carry the property _bf_gift so only ours are ever removed
 *
 * Events: Horizon's cart-items component and cart drawer listen on
 * StandardEvents.cartLinesUpdate (document level) and re-render from the
 * event's promise, so we announce our own add/remove the same way
 * perqen-cart.js does. We reconcile after other components' updates settle.
 */
const cfgEl = document.getElementById('pq-bf-gift-config');
if (cfgEl) {
  const cfg = JSON.parse(cfgEl.textContent);
  const root = cfg.root.endsWith('/') ? cfg.root : cfg.root + '/';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let busy = false;
  let timer;
  /** @type {any} */
  let events = null;
  const money = (cents) => '£' + (cents / 100).toFixed(2);

  async function getCart() {
    const r = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
    return r.json();
  }

  function eligibleSubtotal(cart) {
    return cart.items
      .filter((i) => i.variant_id !== cfg.variantId)
      .reduce((s, i) => s + i.final_line_price, 0);
  }

  function host() {
    return (
      document.querySelector('.cart-drawer__summary') ||
      document.querySelector('.pq-order-summary') ||
      document.querySelector('.cart__summary')
    );
  }

  function render(cart) {
    const h = host();
    if (!h) return;
    let el = document.getElementById('pq-bf-gift-progress');
    if (!el) {
      el = document.createElement('p');
      el.id = 'pq-bf-gift-progress';
      el.style.cssText =
        'margin:0 0 12px;padding:10px 12px;border-radius:6px;background:#0b2d24;color:#fffefb;font-size:14px;line-height:1.4';
      h.prepend(el);
    }
    const sub = eligibleSubtotal(cart);
    const hasGift = cart.items.some((i) => i.variant_id === cfg.variantId);
    if (sub >= cfg.thresholdCents && hasGift) {
      el.textContent = `Your free ${cfg.giftTitle} (${cfg.giftPrice}) is in the basket. Free UK delivery too.`;
    } else if (sub >= cfg.thresholdCents) {
      el.textContent = `Adding your free ${cfg.giftTitle}…`;
    } else {
      el.textContent = `Add ${money(cfg.thresholdCents - sub)} more for a free ${cfg.giftTitle} (${cfg.giftPrice}) and free UK delivery.`;
    }
  }

  /** Announce a cart mutation the way Horizon expects, then run it. */
  async function mutate(action, endpoint, body) {
    let deferred = null;
    if (events?.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new events.CartLinesUpdateEvent({ action, context: 'perqen-bf-gift', lines: [], promise: deferred.promise })
      );
    }
    try {
      const r = await fetch(root + endpoint, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
      const data = await r.json();
      if (deferred) {
        const cart = await getCart();
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'perqen-bf-gift', didError: !r.ok },
        });
      }
      return data;
    } catch (e) {
      deferred?.reject(e);
      return null;
    }
  }

  async function reconcile() {
    if (busy) return;
    busy = true;
    try {
      const cart = await getCart();
      const sub = eligibleSubtotal(cart);
      const giftLines = cart.items.filter((i) => i.variant_id === cfg.variantId);
      const ours = giftLines.filter((i) => i.properties && i.properties._bf_gift);
      if (sub >= cfg.thresholdCents && giftLines.length === 0) {
        await mutate('add', 'cart/add.js', { items: [{ id: cfg.variantId, quantity: 1, properties: { _bf_gift: '1' } }] });
        render(await getCart());
      } else if (sub < cfg.thresholdCents && ours.length) {
        await mutate('remove', 'cart/change.js', { id: ours[0].key, quantity: 0 });
        render(await getCart());
      } else {
        render(cart);
      }
    } catch (e) {
      /* a failed add just means the customer adds the spray by hand; never block the cart */
    } finally {
      busy = false;
    }
  }

  function schedule(delay = 300) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  }

  import('@shopify/events')
    .then((m) => {
      events = m;
      document.addEventListener(m.StandardEvents.cartLinesUpdate, (ev) => {
        if (ev.context === 'perqen-bf-gift') return;
        const p = ev.promise;
        if (p && typeof p.then === 'function') p.then(() => schedule(50), () => {});
        else schedule();
      });
    })
    .catch(() => {
      // Without the events module we still work, just without announcing.
    });

  // The drawer re-renders its summary; put the progress line back when it goes,
  // but only react to mutations inside the drawer, never to the whole page.
  const drawer = document.querySelector('cart-drawer-component') || document.querySelector('.cart-drawer');
  if (drawer) {
    new MutationObserver(() => {
      if (!document.getElementById('pq-bf-gift-progress') && host()) schedule(150);
    }).observe(drawer, { childList: true, subtree: true });
  }
  // One reconcile per page load (a single /cart.js GET): a shopper who arrives
  // with GBP40 already in the basket should not have to touch it first.
  schedule(0);
}
